import { useState, useEffect, useRef } from 'react';
import { useSubscription } from '@apollo/react-hooks';
import CAMPAIGN_LAUNCH_PROGRESS_SUBSCRIPTION from '../GraphQl/Subscriptions/CAMPAIGN_LAUNCH_PROGRESS_SUBSCRIPTION';

const useCampaignLaunchProgress = (campaignOrderId, options = {}) => {
  const { 
    enabled = true,
    onProgress,
    onCompleted,
    onFailed,
    onError,
    autoCleanup = true
  } = options;

  const [progressData, setProgressData] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [estimatedDuration, setEstimatedDuration] = useState(null);
  const cleanup = useRef(null);

  const { data, loading, error } = useSubscription(
    CAMPAIGN_LAUNCH_PROGRESS_SUBSCRIPTION,
    {
      variables: { campaignOrderId },
      skip: !enabled || !campaignOrderId,
      onSubscriptionData: ({ subscriptionData }) => {
        if (subscriptionData?.data?.campaignLaunchProgress) {
          const progress = subscriptionData.data.campaignLaunchProgress;
          
          // Set progress data
          setProgressData(progress);

          // Track if campaign is actively launching
          const activePhases = ['STARTING', 'DATE_VALIDATION', 'INSERTION_ORDER', 'PACKAGE', 'LINE_ITEMS', 'CREATIVES'];
          const isLaunching = activePhases.includes(progress.phase);
          
          if (isLaunching && !isActive) {
            setIsActive(true);
            setStartTime(Date.now());
            if (progress.metadata?.estimatedDuration) {
              setEstimatedDuration(progress.metadata.estimatedDuration);
            }
          }

          // Handle progress callbacks
          if (onProgress) {
            onProgress(progress);
          }

          // Handle completion
          if (progress.phase === 'COMPLETED') {
            setIsActive(false);
            if (onCompleted) {
              onCompleted(progress);
            }
            if (autoCleanup) {
              scheduleCleanup();
            }
          }

          // Handle failure
          if (progress.phase === 'FAILED') {
            setIsActive(false);
            if (onFailed) {
              onFailed(progress);
            }
            if (autoCleanup) {
              scheduleCleanup();
            }
          }
        }
      },
      onError: (subscriptionError) => {
        console.error('Campaign launch progress subscription error:', subscriptionError);
        if (onError) {
          onError(subscriptionError);
        }
      }
    }
  );

  // Schedule cleanup after completion/failure
  const scheduleCleanup = () => {
    if (cleanup.current) {
      clearTimeout(cleanup.current);
    }
    
    cleanup.current = setTimeout(() => {
      setProgressData(null);
      setIsActive(false);
      setStartTime(null);
      setEstimatedDuration(null);
    }, 30000); // Clean up after 30 seconds
  };

  // Calculate elapsed time
  const elapsedTime = startTime ? Date.now() - startTime : 0;
  
  // Calculate estimated remaining time
  const estimatedRemainingTime = 
    estimatedDuration && progressData?.progress > 0
      ? Math.max(0, estimatedDuration - elapsedTime)
      : null;

  // Format time duration in minutes and seconds
  const formatDuration = (milliseconds) => {
    if (!milliseconds) return null;
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (cleanup.current) {
        clearTimeout(cleanup.current);
      }
    };
  }, []);

  return {
    // Progress data
    progressData,
    
    // Status indicators
    isActive,
    isLoading: loading,
    hasError: !!error,
    error,
    
    // Progress details
    phase: progressData?.phase,
    progress: progressData?.progress || 0,
    message: progressData?.message || '',
    step: progressData?.step,
    current: progressData?.current,
    total: progressData?.total,
    metadata: progressData?.metadata,
    
    // Time tracking
    elapsedTime,
    estimatedRemainingTime,
    startTime,
    
    // Formatted times
    elapsedTimeFormatted: formatDuration(elapsedTime),
    estimatedRemainingTimeFormatted: formatDuration(estimatedRemainingTime),
    
    // Helper functions
    isCompleted: progressData?.phase === 'COMPLETED',
    isFailed: progressData?.phase === 'FAILED',
    isPossibleTimeout: progressData?.metadata?.possibleTimeout || false,
    
    // Manual cleanup
    cleanup: () => {
      setProgressData(null);
      setIsActive(false);
      setStartTime(null);
      setEstimatedDuration(null);
    }
  };
};

export default useCampaignLaunchProgress;